// alternative-dashboard/alternative-components/CFOCopilot/SpendByCategoryChart.tsx
import { useEffect, useState } from "react";
import { Cell, Pie, PieChart, Tooltip } from "recharts";
import { useCountry } from "@/app/services/context/CountryContext";
import { gxDashboardSpendCategoryApi } from "@/app/apiConfig/services/customerAbstractApiLayer";
import { gxCurrencyFormat } from "@/app/utils/helpers/gxCurrencyFormat";
import type { DashboardSpendCategoryPayload } from "@/app/types/alterNativeDashboardLayout";
import SubmitLoading from "@/app/components/webloading/errorHandling/SubmitLoading";
import {
  mapSpendCategoryResponse,
  type SpendCategoryUIModel,
} from "../../mappers/spendCategoryMapper";
import "./styles/CustomeChart.scss";

interface SpendByCategoryChartProps {
  title?: string;
  isMandatory?: string;
}

const EMPTY_SPEND_CATEGORY_MODEL: SpendCategoryUIModel = {
  chartData: [],
  totalAmount: 0,
  currency: "AED",
};

const RANGE_MONTHS = 12;

const formatDate = (date: Date): string => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const buildDateRange = () => {
  const toDate = new Date();
  const fromDate = new Date();
  fromDate.setMonth(fromDate.getMonth() - RANGE_MONTHS);
  return { fromDate: formatDate(fromDate), toDate: formatDate(toDate) };
};

const amountFormat = (amount: number) => {
  if (Number.isNaN(amount)) return "0";
  return amount.toLocaleString("en-US");
};

const SpendByCategoryChart = ({ title }: SpendByCategoryChartProps) => {
  const { selectedCountry } = useCountry();
  const currencyCode = selectedCountry?.currency?.code ?? "";

  const [model, setModel] = useState<SpendCategoryUIModel>(
    EMPTY_SPEND_CATEGORY_MODEL,
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);

  useEffect(() => {
    if (!currencyCode) return;

    let isMounted = true;

    const loadSpendByCategory = async () => {
      setIsLoading(true);
      setHasError(false);

      try {
        const { fromDate, toDate } = buildDateRange();

        const payload: DashboardSpendCategoryPayload = {
          currency: currencyCode,
          fromDate,
          toDate,
        };

        const response = await gxDashboardSpendCategoryApi(payload);

        if (!isMounted) return;

        if (response?.status === 1 && response.data) {
          setModel(mapSpendCategoryResponse(response.data, currencyCode));
        } else {
          setModel(EMPTY_SPEND_CATEGORY_MODEL);
        }
      } catch (err) {
        console.log("Error in loadSpendByCategory::", err);

        if (isMounted) {
          setHasError(true);
          setModel(EMPTY_SPEND_CATEGORY_MODEL);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadSpendByCategory();

    return () => {
      isMounted = false;
    };
  }, [currencyCode]);

  const { chartData, totalAmount, currency } = model;

  const currencySymbol = gxCurrencyFormat(0, currency).symbol;

  if (!currencyCode) {
    return (
      <div className="gx-bene-form-container-loading-overlay">
        <SubmitLoading />
      </div>
    );
  }

  return (
    <div className="gx-spend-category-chart">
      <h5 className="gx-p18-b gx-spend-category-chart__title">
        {title || "Spend by category"}
      </h5>

      <div className="gx-spend-category-chart__body relative">
        {isLoading && (
          <div className="gx-bene-form-container-loading-overlay">
            <SubmitLoading />
          </div>
        )}

        {!isLoading && hasError && (
          <p className="gx-p14-r gx-error-text">
            Failed to load spend by category
          </p>
        )}

        {!isLoading && !hasError && chartData.length === 0 && (
          <p className="gx-p14-r gx-text-gray">No spend data available</p>
        )}

        {!isLoading && !hasError && chartData.length > 0 && (
          <>
            <div className="gx-spend-category-chart__donut-wrap">
              <PieChart width={140} height={140}>
                <Pie
                  data={chartData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={45}
                  outerRadius={65}
                  startAngle={90}
                  endAngle={-270}
                  paddingAngle={0}
                  stroke="none"
                >
                  {chartData.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>

                <Tooltip
                  formatter={(value, name) => {
                    const numericValue =
                      typeof value === "number" ? value : Number(value);
                    return [`${amountFormat(numericValue)}%`, name as string];
                  }}
                  contentStyle={{
                    borderRadius: "12px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                  }}
                />
              </PieChart>

              <div className="gx-spend-category-chart__center">
                <span className="gx-spend-category-chart__center-amount">
                  {amountFormat(totalAmount)}
                </span>
                <span className="gx-spend-category-chart__center-label gx-p12-sb">
                  {currencySymbol} Spent
                </span>
              </div>
            </div>

            <div className="gx-spend-category-chart__legend">
              {chartData.map((entry) => (
                <div
                  className="gx-spend-category-chart__legend-row"
                  key={entry.name}
                >
                  <div className="gx-spend-category-chart__legend-item">
                    <span
                      className="gx-spend-category-chart__legend-dot"
                      style={{ backgroundColor: entry.color }}
                    />
                    <span className="gx-spend-category-chart__legend-name gx-p14-sb">
                      {entry.name}
                    </span>
                  </div>

                  <span className="gx-spend-category-chart__legend-percent gx-p14-md">
                    {entry.value}%
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SpendByCategoryChart;
